import { withAuthHeaders } from "../../browser/apiAuthHeaders";

export const ENGINE_EVENTS_SCHEMA = "engine-events.v1" as const;

export const ENGINE_EVENT_KINDS = [
  "stage.started",
  "stage.progress",
  "stage.completed",
  "stage.failed",
  "provider.call",
  "tool.call",
  "tool.result",
  "receipt",
  "flag",
  "warning",
  "pageFailure",
  "cost.rollup"
] as const;

export type EngineEventKind = (typeof ENGINE_EVENT_KINDS)[number];

export type KnownEngineStageId =
  | "classify"
  | "takeoff"
  | "projection"
  | "points"
  | "controlsSpec"
  | "panel";

/** The server may introduce a stage before this UI knows how to name it. */
export type EngineStageId = KnownEngineStageId | (string & {});

export interface EngineArtifactRef {
  path: string;
  sha256: string;
}

export interface EngineMeter {
  inputTokens: number;
  outputTokens: number;
  completedCalls: number;
  costGbp: number;
}

export interface EngineFlag {
  flagId: string;
  kind: string;
  status: string;
  statement: string;
  affectedRefs: readonly string[];
  basis: string;
  nextAction: string;
}

interface EnginePayload {
  readonly [key: string]: unknown;
}

export interface EngineStageLifecyclePayload extends EnginePayload {
  status?: string;
  reason?: string;
  artifactRefs?: readonly EngineArtifactRef[];
  meter?: EngineMeter;
  flags?: readonly EngineFlag[];
}

export interface EngineStageProgressPayload extends EnginePayload {
  note: string;
  unitCounters?: Readonly<Record<string, number>>;
}

export interface EngineProviderCallPayload extends EnginePayload {
  model: string;
  promptTokens: number;
  completionTokens: number;
  costGbp: number;
  durationMs: number;
  attempt: number;
  retry: boolean;
  ok: boolean;
}

export interface EngineToolCallPayload extends EnginePayload {
  toolName: string;
  argsDigest: string;
  durationMs?: number;
  attempt?: number;
  ok?: boolean;
}

export interface EngineToolResultPayload extends EnginePayload {
  toolName: string;
  resultDigest: string;
  durationMs: number;
  ok: boolean;
}

export interface EngineReceiptPayload extends EnginePayload {
  receiptKind: string;
  subject: string;
  validity: boolean;
  mismatchDetail?: string;
}

export interface EngineFlagPayload extends EnginePayload, EngineFlag {}

export interface EngineWarningPayload extends EnginePayload {
  message: string;
  recoverable: boolean;
}

export interface EnginePageFailurePayload extends EnginePayload {
  message: string;
  recoverable: boolean;
}

export interface EngineCostRollupPayload extends EngineMeter, EnginePayload {}

interface EngineEventBase<K extends EngineEventKind, P extends EnginePayload> {
  seq: number;
  ts: string;
  runId: string;
  stageId: EngineStageId;
  ordinal: number;
  docId?: string;
  page?: number;
  kind: K;
  payload: P;
}

export type EngineEvent =
  | EngineEventBase<
      "stage.started" | "stage.completed" | "stage.failed",
      EngineStageLifecyclePayload
    >
  | EngineEventBase<"stage.progress", EngineStageProgressPayload>
  | EngineEventBase<"provider.call", EngineProviderCallPayload>
  | EngineEventBase<"tool.call", EngineToolCallPayload>
  | EngineEventBase<"tool.result", EngineToolResultPayload>
  | EngineEventBase<"receipt", EngineReceiptPayload>
  | EngineEventBase<"flag", EngineFlagPayload>
  | EngineEventBase<"warning", EngineWarningPayload>
  | EngineEventBase<"pageFailure", EnginePageFailurePayload>
  | EngineEventBase<"cost.rollup", EngineCostRollupPayload>;

export interface EngineEventsPage {
  events: readonly EngineEvent[];
  highWaterMark: number;
  terminal?: boolean;
  /** App-safe retained engine outputs; keys are omitted when no artifact exists. */
  engineResults?: Readonly<Record<string, unknown>>;
}

export interface FetchEngineEventsOptions {
  fetcher?: typeof fetch;
  signal?: AbortSignal;
}

export interface StartEngineRunResponse {
  ok: true;
  runId: string;
}

export interface CancelEngineRunOptions {
  fetcher?: typeof fetch;
  reason?: string;
}

export interface EngineRunCancelledResponse {
  outcome: "cancelled";
  runId: string;
  status: "cancelled";
  cancelledAt: string;
  cancelledStageId: string | null;
}

export interface EngineRunAlreadyTerminalResponse {
  outcome: "already-terminal";
  runId: string;
  status: string;
}

export type CancelEngineRunResponse =
  | EngineRunCancelledResponse
  | EngineRunAlreadyTerminalResponse;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isEventKind = (value: unknown): value is EngineEventKind =>
  typeof value === "string" &&
  (ENGINE_EVENT_KINDS as readonly string[]).includes(value);

const invalidPage = (message: string): never => {
  throw new Error(`Invalid ${ENGINE_EVENTS_SCHEMA} response: ${message}`);
};

const parseEvent = (value: unknown, index: number): EngineEvent => {
  if (!isRecord(value)) {
    return invalidPage(`event ${index + 1} is not an object`);
  }

  if (typeof value.seq !== "number" || !Number.isInteger(value.seq) || value.seq < 0) {
    return invalidPage(`event ${index + 1} has an invalid seq`);
  }

  if (typeof value.ts !== "string" || value.ts.length === 0) {
    return invalidPage(`event ${index + 1} has an invalid ts`);
  }

  if (typeof value.runId !== "string" || value.runId.length === 0) {
    return invalidPage(`event ${index + 1} has an invalid runId`);
  }

  if (typeof value.stageId !== "string" || value.stageId.length === 0) {
    return invalidPage(`event ${index + 1} has an invalid stageId`);
  }

  if (
    typeof value.ordinal !== "number" ||
    !Number.isInteger(value.ordinal) ||
    value.ordinal < 0
  ) {
    return invalidPage(`event ${index + 1} has an invalid ordinal`);
  }

  if (!isEventKind(value.kind)) {
    return invalidPage(`event ${index + 1} has an unknown kind`);
  }

  if (!isRecord(value.payload)) {
    return invalidPage(`event ${index + 1} has an invalid payload`);
  }

  if (value.docId !== undefined && typeof value.docId !== "string") {
    return invalidPage(`event ${index + 1} has an invalid docId`);
  }

  if (
    value.page !== undefined &&
    (typeof value.page !== "number" || !Number.isInteger(value.page) || value.page < 1)
  ) {
    return invalidPage(`event ${index + 1} has an invalid page`);
  }

  return value as unknown as EngineEvent;
};

export function parseEngineEventsPage(value: unknown, sinceSeq = -1): EngineEventsPage {
  if (!isRecord(value) || !Array.isArray(value.events)) {
    return invalidPage("events must be an array");
  }

  if (
    typeof value.highWaterMark !== "number" ||
    !Number.isInteger(value.highWaterMark) ||
    value.highWaterMark < -1
  ) {
    return invalidPage("highWaterMark must be a non-negative integer");
  }

  const events = value.events.map(parseEvent);
  let previousSeq = sinceSeq;

  for (const event of events) {
    if (event.seq !== previousSeq + 1) {
      return invalidPage("events must be gap-free after sinceSeq");
    }

    previousSeq = event.seq;
  }

  const lastSeq = events.at(-1)?.seq ?? sinceSeq;

  if (value.highWaterMark < lastSeq) {
    return invalidPage("highWaterMark cannot be behind the returned events");
  }

  return {
    events,
    highWaterMark: value.highWaterMark,
    ...(typeof value.terminal === "boolean" ? { terminal: value.terminal } : {}),
    ...(isRecord(value.engineResults) ? { engineResults: value.engineResults } : {})
  };
}

/**
 * The endpoint path is intentionally isolated here. The transport lane can
 * change this one function when its route is ready; the surface consumes only
 * EngineEventsPage and never knows about HTTP details.
 */
export function engineEventsUrl(runId: string, sinceSeq: number): string {
  return `/api/engine-runs/${encodeURIComponent(runId)}/events?sinceSeq=${encodeURIComponent(String(sinceSeq))}`;
}

export function engineRunStartUrl(projectId: string): string {
  return `/api/projects/${encodeURIComponent(projectId)}/engine-runs`;
}

export function engineRunCancelUrl(runId: string): string {
  return `/api/engine-runs/${encodeURIComponent(runId)}/cancel`;
}

export async function startEngineRun(
  projectId: string,
  options: { fetcher?: typeof fetch } = {}
): Promise<StartEngineRunResponse> {
  const fetcher = options.fetcher ?? globalThis.fetch;

  if (!fetcher) {
    throw new Error("Engine run transport is unavailable in this environment.");
  }

  const response = await fetcher(engineRunStartUrl(projectId), {
    method: "POST",
    headers: await withAuthHeaders({
      accept: "application/json",
      "content-type": "application/json"
    }),
    body: "{}"
  });
  const payload = (await response.json()) as Record<string, unknown>;

  if (!response.ok) {
    const message =
      typeof payload.error === "string"
        ? payload.error
        : `Engine run start returned HTTP ${response.status}.`;
    throw new Error(message);
  }

  if (payload.ok !== true || typeof payload.runId !== "string" || !payload.runId) {
    throw new Error("Engine run start returned an invalid run id.");
  }

  return { ok: true, runId: payload.runId };
}

export async function cancelEngineRun(
  runId: string,
  options: CancelEngineRunOptions = {}
): Promise<CancelEngineRunResponse> {
  const fetcher = options.fetcher ?? globalThis.fetch;

  if (!fetcher) {
    throw new Error(
      "Engine run cancellation transport is unavailable in this environment."
    );
  }

  const response = await fetcher(engineRunCancelUrl(runId), {
    method: "POST",
    headers: await withAuthHeaders({
      accept: "application/json",
      "content-type": "application/json"
    }),
    body: JSON.stringify({
      reason: options.reason ?? "Cancelled from the run progress view."
    })
  });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  const code =
    typeof payload.code === "string"
      ? payload.code
      : typeof payload.error === "string"
        ? payload.error
        : undefined;

  if (response.status === 409 && code === "ENGINE_RUN_ALREADY_TERMINAL") {
    if (typeof payload.status !== "string" || payload.status.length === 0) {
      throw new Error("Engine run cancellation returned no terminal status.");
    }

    return {
      outcome: "already-terminal",
      runId,
      status: payload.status
    };
  }

  if (!response.ok) {
    throw new Error(`Engine run cancellation returned HTTP ${response.status}.`);
  }

  if (
    payload.runId !== runId ||
    payload.status !== "cancelled" ||
    typeof payload.cancelledAt !== "string" ||
    (payload.cancelledStageId !== null && typeof payload.cancelledStageId !== "string")
  ) {
    throw new Error("Engine run cancellation returned an invalid response.");
  }

  return {
    outcome: "cancelled",
    runId,
    status: "cancelled",
    cancelledAt: payload.cancelledAt,
    cancelledStageId: payload.cancelledStageId
  };
}

export async function fetchEngineEventsPage(
  runId: string,
  sinceSeq: number,
  options: FetchEngineEventsOptions = {}
): Promise<EngineEventsPage> {
  const fetcher = options.fetcher ?? globalThis.fetch;

  if (!fetcher) {
    throw new Error("Engine event transport is unavailable in this environment.");
  }

  const response = await fetcher(engineEventsUrl(runId, sinceSeq), {
    headers: await withAuthHeaders({ accept: "application/json" }),
    ...(options.signal ? { signal: options.signal } : {})
  });

  if (!response.ok) {
    throw new Error(`Engine event transport returned HTTP ${response.status}.`);
  }

  return parseEngineEventsPage(await response.json(), sinceSeq);
}
